import { useEffect, useState, useCallback, useRef } from 'react';
import dayjs from 'dayjs';

import AddProduct from '../../components/product/add-product/add-product.component';
import Button from '../../components/reusable/button/button.component';
import ComponentModal from '../../components/reusable/component-modal/component-modal.component';
import Pagination from '../../components/reusable/pagination/pagination.component';
import ProductsTable from '../../components/reusable/tables/products-table/products-table.component';
import Spinner from '../../components/reusable/spinner/spinner.component';
import TableControls from '../../components/reusable/table-controls/table-controls.component';

import useTableState from '../../hooks/useTableState';
import Client from '../../tools/client';

import {
    ContentContainer,
    MainTitle
} from '../../styles/page.styles';

const client = new Client();

const ProductsPage = () => {
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [productsCount, setProductsCount] = useState(null);
    const [showAddProduct, setShowAddProduct] = useState(false);

    const {
        page,
        size,
        search,
        sortKey,
        sortDirection,
        setPage,
        setSize,
        setSearch,
        setSortKey,
        setSortDirection
    } = useTableState({ defaultSize: 25 });

    const isInitialMount = useRef(true);

    const getProducts = useCallback(async () => {
        setLoading(true);
        let query = `?page=${page}&size=${size}`;
        if (search) query += `&search=${encodeURIComponent(search)}`;
        if (sortDirection) query += `&sortDirection=${sortDirection}`;
        if (sortKey) query += `&sortKey=${sortKey}`;
        const res = await client.getProducts(query);
        setProducts(res.rows);
        setProductsCount(res.count);
        setLoading(false);
    }, [page, size, search, sortKey, sortDirection]);

    useEffect(() => {
        getProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        getProducts();
    }, [page, size, search, sortKey, sortDirection, getProducts]);

    const handleSearchSubmit = useCallback(() => {}, []);

    // Export configuration
    const exportFn = async () => {
        const res = await client.exportProducts();
        return res.rows || [];
    };

    const formatExportData = (data) => {
        return data.map((product) => {
            const createdDate = dayjs(product.createdAt).format('MM/DD/YYYY');
            return [
                product.id || '',
                product.name || '',
                product?.Category?.name || product['Category.name'] || '',
                product.description || '',
                createdDate
            ];
        });
    };

    const exportHeaders = ['ID', 'Name', 'Category', 'Description', 'Created Date'];

    return (
        <>
            <ComponentModal isOpen={showAddProduct} onClose={() => setShowAddProduct(false)} title="Add Product">
                <AddProduct
                    onClose={() => setShowAddProduct(false)}
                    onSuccess={() => {
                        setShowAddProduct(false);
                        getProducts();
                    }}
                />
            </ComponentModal>

            <ContentContainer>
                <MainTitle>Products</MainTitle>

                <TableControls
                    search={search}
                    setSearch={setSearch}
                    onSearchSubmit={handleSearchSubmit}
                    size={size}
                    setSize={setSize}
                    entityName="products"
                    exportFn={exportFn}
                    formatExportData={formatExportData}
                    exportHeaders={exportHeaders}
                    additionalButtons={
                        <Button onClick={() => setShowAddProduct(true)}>Add Product</Button>
                    }
                />

                {loading ? (
                    <Spinner />
                ) : (
                    <>
                        <ProductsTable
                            products={products}
                            sortKey={sortKey}
                            setSortKey={setSortKey}
                            sortDirection={sortDirection}
                            setSortDirection={setSortDirection}
                        />

                        <Pagination
                            count={productsCount}
                            size={size}
                            page={page}
                            changePage={setPage}
                        />
                    </>
                )}
            </ContentContainer>
        </>
    );
};

export default ProductsPage;
